import {
  AdapterMessage,
  AdapterReply,
  MinimalProviderConfig,
  ModelInfo,
  OnChunk,
  createProviderApiError,
  normalizeEndpoint,
  recordLiveContextTokens,
  safeErrorDetail,
  wrapNetworkError,
} from "./types";
import { httpRequest, httpStream, HttpResponse, HttpStreamResponse } from "../http";

// Anthropic's Messages API shape is different enough from OpenAI's to need its own translator
// (same reasoning as gemini.ts): auth uses an "x-api-key" header instead of "Authorization",
// a required "anthropic-version" header, a top-level "system" field instead of a system
// message, "max_tokens" is required (there's no provider-default like OpenAI/Gemini have), and
// replies come back as a list of content blocks rather than a single string.

const ANTHROPIC_VERSION = "2023-06-01";
// Last-resort fallback when a provider doesn't set its own MinimalProviderConfig.maxOutputTokens
// (Add/Edit Provider's "Max reply length" field) AND this specific model's real limit was never
// learned via liveMaxOutputTokensCache below (e.g. an unlisted/custom model name, or a fresh app
// load where the model combobox was never opened for this provider) — Anthropic has no "as long
// as you need" option, so *something* has to be sent. Exported so providerForm.ts can show it in
// that field's placeholder/tooltip for this narrow case.
export const DEFAULT_ANTHROPIC_MAX_TOKENS = 4096;
const MAX_TOKENS = DEFAULT_ANTHROPIC_MAX_TOKENS;

// Remembers each model's real max_tokens (Anthropic's own Models API, read in listModels below)
// for the rest of this app session — in-memory only, never written into ProviderConfig itself,
// so it stays genuinely dynamic (a model's limit can change without needing anyone to re-save a
// provider) rather than being snapshotted the way maxContextTokens/maxOutputTokens deliberately
// aren't (see CLAUDE.md's "Max-context-tokens: how the default is resolved"). Populated whenever
// listModels() runs — which happens every time the model dropdown is opened in Add/Edit Provider
// or New chat/Edit session — so by the time a real send happens, this is populated for any model
// actually picked from that list. Keyed by the raw (unnormalized) endpoint string, same value
// both listModels() and sendMessage() receive on MinimalProviderConfig.endpoint, so a lookup
// always matches what was cached without needing to normalize on both sides.
const liveMaxOutputTokensCache = new Map<string, number>();

function cacheKey(endpoint: string, model: string): string {
  return `${endpoint} ${model}`;
}

function authHeaders(apiKey: string): { [key: string]: string } {
  return {
    "x-api-key": apiKey,
    "anthropic-version": ANTHROPIC_VERSION,
    // Anthropic's API only returns browser-permissive CORS headers when this is present —
    // without it, a direct request from this task pane (a browser context) is silently blocked.
    "anthropic-dangerous-direct-browser-access": "true",
  };
}

// One loose shape covering every field this app reads across Anthropic's different SSE event
// types (message_start/content_block_delta/message_delta) — fields don't collide across event
// types, so one structural type is simpler than a discriminated union for what's read here.
interface AnthropicStreamFrame {
  message?: { usage?: { input_tokens?: unknown } };
  delta?: { type?: string; text?: unknown; stop_reason?: unknown };
  usage?: { output_tokens?: unknown };
}

// `signal` is the Stop button / typing-bubble click (chat.ts) — see openaiCompatible.ts's
// sendMessage for the same comment, applies identically here. `onChunk` (optional) fires with
// each incremental slice of reply text as it streams in — see adapters/types.ts's OnChunk.
//
// Streams via SSE (`stream: true`). Event sequence: `message_start` (carries the prompt's
// input_tokens up front) → repeated `content_block_delta` (`delta.type === "text_delta"`,
// `delta.text` = the actual incremental reply text — the only event this function's `onChunk`
// fires from) → `message_delta` (carries the final output_tokens and stop_reason once generation
// ends) → `message_stop`. `content_block_start`/`content_block_stop`/`ping` and any other event
// name are silently ignored — nothing this app needs lives in them.
export async function sendMessage(
  config: MinimalProviderConfig,
  messages: AdapterMessage[],
  signal?: AbortSignal,
  onChunk?: OnChunk
): Promise<AdapterReply> {
  const url = `${normalizeEndpoint(config.endpoint)}/messages`;
  // Explicit override (Add/Edit Provider or a session's own Edit-session override) wins outright;
  // otherwise prefer this model's real fetched limit over the flat fallback, if it's been learned
  // this session — see liveMaxOutputTokensCache's own comment for why blank doesn't mean "we
  // don't know," it means "use the largest this model actually supports."
  const maxTokens =
    config.maxOutputTokens ??
    liveMaxOutputTokensCache.get(cacheKey(config.endpoint, config.model)) ??
    MAX_TOKENS;

  let response: HttpStreamResponse;
  try {
    response = await httpStream(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(config.apiKey),
      },
      body: JSON.stringify({
        model: config.model,
        max_tokens: maxTokens,
        messages: messages.map((m) => ({ role: m.role, content: m.content })),
        stream: true,
        ...(config.temperature !== undefined ? { temperature: config.temperature } : {}),
        // Anthropic keeps system instructions in their own top-level field, separate from the
        // conversation turns — not just another message in the history.
        ...(config.systemPrompt ? { system: config.systemPrompt } : {}),
      }),
      signal,
    });
  } catch (err) {
    wrapNetworkError(config.name, err);
  }

  if (!response.ok) {
    throw createProviderApiError(
      `${config.name} returned an error (${response.status}): ${await safeErrorDetail(response)}`
    );
  }

  let text = "";
  let inputTokens: number | undefined;
  let outputTokens: number | undefined;
  let stopReason: string | undefined;
  try {
    for await (const frame of response.events()) {
      let parsed: AnthropicStreamFrame;
      try {
        parsed = JSON.parse(frame.data);
      } catch {
        // A malformed/unexpected frame shouldn't fail an otherwise-good reply — skip it rather
        // than aborting text already received.
        continue;
      }
      if (frame.event === "message_start") {
        if (typeof parsed.message?.usage?.input_tokens === "number") {
          inputTokens = parsed.message.usage.input_tokens;
        }
      } else if (frame.event === "content_block_delta") {
        if (parsed.delta?.type === "text_delta" && typeof parsed.delta.text === "string") {
          const delta = parsed.delta.text;
          if (delta) {
            text += delta;
            onChunk?.(delta);
          }
        }
      } else if (frame.event === "message_delta") {
        if (typeof parsed.usage?.output_tokens === "number") {
          outputTokens = parsed.usage.output_tokens;
        }
        if (typeof parsed.delta?.stop_reason === "string") {
          stopReason = parsed.delta.stop_reason;
        }
      }
    }
  } catch (err) {
    wrapNetworkError(config.name, err);
  }

  if (!text) {
    throw createProviderApiError(`${config.name} returned a response in an unexpected shape.`);
  }

  // Unlike a request that's too long (which errors up front), hitting the max_tokens ceiling
  // above just silently stops generation mid-reply — "stop_reason": "max_tokens" is the only
  // signal this happened (now read from the message_delta event rather than the single
  // non-streaming response body). Surfaced directly in the returned text so a reply cut off
  // mid-scene reads as a hit limit, not a model that just stopped for no reason.
  const finalText =
    stopReason === "max_tokens"
      ? `${text}\n\n[Reply cut off — reached the ${maxTokens}-token output limit.]`
      : text;

  const usage =
    typeof inputTokens === "number" && typeof outputTokens === "number"
      ? { inputTokens, outputTokens }
      : undefined;

  return { text: finalText, usage };
}

export async function listModels(
  config: Pick<MinimalProviderConfig, "endpoint" | "apiKey">
): Promise<ModelInfo[]> {
  const url = `${normalizeEndpoint(config.endpoint)}/models`;

  let response: HttpResponse;
  try {
    response = await httpRequest(url, {
      headers: authHeaders(config.apiKey),
    });
  } catch (err) {
    throw createProviderApiError(`Network error fetching model list: ${(err as Error).message}`);
  }

  if (!response.ok) {
    throw createProviderApiError(
      `Could not fetch model list (${response.status}): ${await safeErrorDetail(response)}`
    );
  }

  const data = (await response.json()) as { data?: unknown };
  const list = data?.data;
  if (!Array.isArray(list)) {
    throw createProviderApiError("Model list response was in an unexpected shape.");
  }
  // max_input_tokens is the real context window straight from Anthropic's own API — used as
  // the live tier in getDefaultMaxContextTokens (storage.ts) instead of a guess. max_tokens
  // (2026-08-11) is the same idea for the reply-length cap — Anthropic's Models API returns
  // both fields side by side, but only max_input_tokens was being read; this was previously
  // falling back to a single flat constant (DEFAULT_ANTHROPIC_MAX_TOKENS, 4096) for every model
  // regardless of what that model can actually output (current models support up to 128K+),
  // which is why "Max reply length" needed manually raising for anything long. Real per-model
  // data removes that need for most users — see storage.ts's getDefaultMaxOutputTokens.
  const models = list
    .map(
      (m: { id?: unknown; max_input_tokens?: unknown; max_tokens?: unknown }): ModelInfo | null =>
        typeof m?.id === "string"
          ? {
              id: m.id,
              contextTokens:
                typeof m.max_input_tokens === "number" ? m.max_input_tokens : undefined,
              maxOutputTokens: typeof m.max_tokens === "number" ? m.max_tokens : undefined,
            }
          : null
    )
    .filter((info: ModelInfo | null): info is ModelInfo => info !== null)
    .sort((a, b) => a.id.localeCompare(b.id));

  // Populate liveMaxOutputTokensCache (see its own comment) so a later sendMessage for any of
  // these models can use the real limit instead of the flat fallback, without a second fetch.
  // recordLiveContextTokens (adapters/types.ts) is the equivalent for Max context — shared
  // across all three adapters since storage.ts's getTruncatedHistory needs to read it, unlike
  // this file-local cache which only ever needed to live inside anthropic.ts itself.
  for (const model of models) {
    if (model.maxOutputTokens !== undefined) {
      liveMaxOutputTokensCache.set(cacheKey(config.endpoint, model.id), model.maxOutputTokens);
    }
    recordLiveContextTokens(config.endpoint, model.id, model.contextTokens);
  }

  return models;
}
